import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from 'store/store';
import { AdminList } from '../AdminList';
import { Button, DisplayItemsPerPageSelect, Paginator, SecondaryButton } from 'components';
import { StyledAdminEditPoolConfigs } from './styles';
import { RefreshButton } from '../RefreshButton';
import { useSelector } from 'react-redux';
import { AdminPoolConfigsListHeader } from '../AdminPoolConfigsListHeader';
import { AdminPoolConfigsListRow } from '../AdminPoolConfigsListRow';
import {
  poolConfigsListSetPage,
  selectAdminPoolConfigs,
  setItemsPerPoolConfigsListPage,
} from 'store/slices/adminPoolConfigsSlice';
import { fetchPoolConfigs } from 'store/thunks/adminPoolConfigsThunk';
import { AdminConfirmModal } from '../AdminConfirmModal';
import { useEffect, useState } from 'react';
import { AdminAddModal } from '../AdminAddModal';
import { AdminAddPoolConfigForm } from '../AdminAddPoolConfigForm';
import { IPoolConfig } from 'types';
import {
  VALIDATION_ERROR_ADMIN_POOL_ALGORITHM_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_HOST_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_NAME_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_PASSWORD_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_PORT_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_USER_REQUIRED,
} from 'constants/errorsConstants';
import { AdminRepository } from 'repositories/AdminRepository';
import { EApiResponseStatus } from 'types/api';
import {fetchGroupPoolConfigs, fetchGroupPoolConfigsAll} from '../../../../store/thunks/adminGroupPoolConfigsThunk';

interface IAdminEditPoolConfigsProps {
  children?: React.ReactNode;
}

export const AdminEditPoolConfigs = ({ children }: IAdminEditPoolConfigsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { poolConfigs } = useSelector(selectAdminPoolConfigs);
  const { token } = useAppSelector((state) => state.auth);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormState, setAddFormState] = useState({
    name: '',
    host: '',
    port: '',
    algorithm: '',
    user: '',
    password: '',
  });
  const [poolConfigsChanges, setPoolConfigsChanges] = useState(false);
  const [editedConfigs, setEditedConfigs] = useState<IPoolConfig[]>(poolConfigs);
  const { pagesQuantity, currentPage, itemsPerPage } = useSelector(
    (state: RootState) => state.adminPoolConfigs,
  );

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const submitAddPoolConfigHandler = async () => {
    setShowAddModal(true);
  };

  const submitAddAction = async () => {
    submitAddPoolConfig();
    setShowAddModal(false);
  };

  const submitAddPoolConfig = async () => {
    try {
      if (!token) throw Error('no token or unathorised');
      const { name, host, port, algorithm, user, password } = addFormState;

      if (!name.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_NAME_REQUIRED);
      if (!host.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_HOST_REQUIRED);
      if (!port.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_PORT_REQUIRED);
      if (!algorithm.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_ALGORITHM_REQUIRED);
      if (!user.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_USER_REQUIRED);
      if (!password.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_PASSWORD_REQUIRED);

      const response = await AdminRepository.addPoolConfig(token, {
        name,
        host,
        port,
        user,
        password,
        algorithm,
      });

      if (response.status === EApiResponseStatus.FAILURE) {
        throw new Error(response.error);
      }

      await dispatch(fetchPoolConfigs());
      await dispatch(fetchGroupPoolConfigsAll());
    } catch (error) {
      console.error('submit add pool config error', error);
    }
  };

  const submitDeletePoolConfig = async (poolId: number) => {
    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof poolId !== 'number') throw Error(VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED);

      const response = await AdminRepository.deletePoolConfig(token, poolId);

      if (response.status === EApiResponseStatus.FAILURE) {
        throw new Error(response.error);
      }

      await dispatch(fetchPoolConfigs());
      await dispatch(fetchGroupPoolConfigsAll());
    } catch (error) {
      console.error('submit delete pool config error', error);
    }
  };

  const submitSaveChangesHandler = async () => {
    if (editedConfigs.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');

      const requests = editedConfigs
        .map((config) => {
          const poolConfigItem = poolConfigs.find((item) => item.id === config.id);
          if (!poolConfigItem) throw new Error(`Pool config onfig with ID ${config.id} not found`);

          const hasChanges = JSON.stringify(config) !== JSON.stringify(poolConfigItem);
          return hasChanges ? config : null;
        })
        .filter(Boolean)
        .map((config) => AdminRepository.setPoolConfig(token, config!));

      if (requests.length === 0) return;

      const responses = await Promise.all(requests);

      const failedResponse = responses.find((res) => res.status === EApiResponseStatus.FAILURE);
      if (failedResponse) throw new Error(failedResponse.error);

      await dispatch(fetchPoolConfigs());
    } catch (error) {
      console.error('submit group pool config save changes error', error);
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const submitDiscardChangesActionHandler = () => {
    setShowConfirmModal(true);
  };

  const submitDiscardChangesAction = async () => {
    setEditedConfigs(poolConfigs);
    setPoolConfigsChanges(false);
    setShowConfirmModal(false);
  };

  const refreshButtonHandler = async () => {
    dispatch(fetchPoolConfigs());
  };

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(poolConfigsListSetPage(currentPage));
    dispatch(fetchPoolConfigs());
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerPoolConfigsListPage(itemsQuantity));
    dispatch(fetchPoolConfigs());
  };

  useEffect(() => {
    const hasChanges = JSON.stringify(poolConfigs) !== JSON.stringify(editedConfigs);
    setPoolConfigsChanges(hasChanges);
  }, [editedConfigs, poolConfigs]);

  useEffect(() => {
    setEditedConfigs(poolConfigs);
  }, [poolConfigs]);

  return (
    <StyledAdminEditPoolConfigs>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-pool-configs-controls">
            <Button
              title={t('adminPage.editPoolConfigs.addPoolConfig')}
              onClick={submitAddPoolConfigHandler}
            />
            {showAddModal && (
              <AdminAddModal
                title={t('adminPage.confirmModal.addPoolConfig')}
                onConfirm={submitAddAction}
                onCancel={closeAddModal}
                show={showAddModal}
              >
                <AdminAddPoolConfigForm config={addFormState} onChange={setAddFormState} />
              </AdminAddModal>
            )}
            {poolConfigsChanges && (
              <>
                <Button
                  title={t('adminPage.editPoolConfigs.saveChanges')}
                  onClick={submitSaveChangesHandler}
                />
                <div className="admin-edit-pool-configs-changes-wrapper">
                  <SecondaryButton
                    title={t('adminPage.editPoolConfigs.discardChanges')}
                    outline
                    onClick={submitDiscardChangesActionHandler}
                  />
                  <div className="admin-edit-pool-configs-changes-badge">
                    {t('adminPage.editPoolConfigs.unsavedChanges')}
                  </div>
                </div>
              </>
            )}
            {showConfirmModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.discardChanges')}
                onConfirm={submitDiscardChangesAction}
                onCancel={closeConfirmModal}
                show={showConfirmModal}
              />
            )}
            <div className="admin-edit-pool-configs-controls__right">
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="admin-edit-pool-configs-list-body">
          <AdminPoolConfigsListHeader />
          {editedConfigs.map((config) => (
            <AdminPoolConfigsListRow
              key={config.id}
              config={config}
              onChange={(updatedConfig) => {
                setEditedConfigs((prev) =>
                  prev.map((item) => (item.id === updatedConfig.id ? updatedConfig : item)),
                );
              }}
              onDelete={submitDeletePoolConfig}
            />
          ))}
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditPoolConfigs>
  );
};
